import { useState, type Ref } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { exportActionTooltips } from '../../../shared/config/actionHelpCatalog';
import type { GroupBy } from '../../../shared/types/pcfExport.types';
import { getExportRailQuietIconButtonSx } from './exportRailControlStyles';

interface PcfVisualCheckMenuProps {
  busy: boolean;
  parameterName: string;
  parameterSource: 'Element' | 'Type';
  buttonRef?: Ref<HTMLButtonElement>;
  onApply: (mode: GroupBy) => void;
  onReset: () => void;
}

export default function PcfVisualCheckMenu({
  busy,
  parameterName,
  parameterSource,
  buttonRef,
  onApply,
  onReset,
}: PcfVisualCheckMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const configuredParameter = parameterName.trim();

  const selectMode = (mode: GroupBy) => {
    setAnchor(null);
    onApply(mode);
  };

  return (
    <>
      <ActionHelpTooltip
        help={exportActionTooltips.visualCheck}
        disabledHelp={exportActionTooltips.visualCheckBusy}
        disabled={busy}
      >
        <IconButton
          ref={buttonRef}
          size="small"
          aria-label="Visual check"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          disabled={busy}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={getExportRailQuietIconButtonSx(theme)}
        >
          {busy ? <OperationProgressIndicator size={16} ariaLabel="Applying visual check" /> : <VisibilityIcon fontSize="small" />}
        </IconButton>
      </ActionHelpTooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ 'aria-label': 'Visual check modes' }}
      >
        <MenuItem onClick={() => selectMode('SystemName')}>System Name</MenuItem>
        <MenuItem onClick={() => selectMode('SystemTypeName')}>System Type Name</MenuItem>
        <Tooltip
          placement="right"
          title={configuredParameter ? '' : 'Configure a grouping parameter in Export settings first.'}
        >
          <span>
            <MenuItem
              disabled={!configuredParameter}
              onClick={() => selectMode('Parameter')}
              sx={{ minWidth: 260 }}
            >
              <ListItemText
                primary="Parameter"
                secondary={configuredParameter
                  ? `${configuredParameter} (${parameterSource})`
                  : 'Configure in Export settings'}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); onReset(); }}>
          Reset visual check
        </MenuItem>
      </Menu>
    </>
  );
}
